import { useId, type SelectHTMLAttributes } from 'react';

import { cn } from '@/lib/cn';

export interface SelectOption {
  value: string;
  label: string;
}

export interface SelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'onChange' | 'value'> {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  options: SelectOption[];
}

export default function Select({ label, value, onChange, options, disabled, id, className, ...rest }: SelectProps) {
  const autoId = useId();
  const inputId = id ?? autoId;
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      {label && (
        <label htmlFor={inputId} className="text-xs font-medium tracking-wide text-(--text-secondary) uppercase">
          {label}
        </label>
      )}
      <select
        id={inputId}
        value={value}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
        className={cn(
          'h-10 rounded-lg border border-(--border-default) bg-(--bg-surface) px-3 text-sm text-(--text-primary) transition-colors',
          'hover:bg-(--bg-surface-hover)',
          'focus-visible:ring-2 focus-visible:ring-(--accent-primary) focus-visible:ring-offset-1 focus-visible:ring-offset-(--bg-surface-strong) focus-visible:outline-none',
          'disabled:cursor-not-allowed disabled:opacity-50',
        )}
        {...rest}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </div>
  );
}
